import React, { useEffect, useState } from "react";
import Spinner from "../component/Spinner";
import ProfileAvatar from '../component/ProfileAvtar';
import { useNavigate } from 'react-router-dom';
import ConfirmationPopup from '../component/ConfirmationPopup';
import { Trash2, Calendar, Plus } from 'lucide-react';
import axiosInstance from '../networking/axiosconfig';
import Navbar from './Navbar';

const Leavelist = () => {
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isPopupVisible, setPopupVisible] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const navigate = useNavigate();
    const userId = localStorage.getItem("userId");
    const apiUrl = `/api/leave/${userId}`;

    const groupLeavesByGroupId = (leaves) => {
        const groupedLeaves = {};

        leaves.forEach((leave) => {
            const groupId = leave.leave_group_id;

            if (!groupedLeaves[groupId]) {
                groupedLeaves[groupId] = {
                    ...leave,
                    dates: [new Date(leave.date)],
                };
            } else {
                groupedLeaves[groupId].dates.push(new Date(leave.date));
            }
        });

        return Object.values(groupedLeaves).map((leave) => ({
            ...leave,
            start_date: leave.dates[0],
            end_date: leave.dates[leave.dates.length - 1],
            formatted_date: `${leave.dates[0].toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            })} - ${leave.dates[leave.dates.length - 1].toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            })}`,
        }));
    };

    const fetchLeaveData = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(apiUrl);
            const leaveList = res.data?.data?.leaveList || [];

            if (Array.isArray(leaveList)) {
                const groupedLeaves = groupLeavesByGroupId(leaveList);
                setLeaves(groupedLeaves);
            } else {
                console.error("Invalid API Response Format:", res.data);
            }
        } catch (err) {
            console.error("API Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeaveData();
    }, []);

    const handleDelete = async () => {
        if (!selectedId) return console.error("Leave ID is missing!");

        try {
            await axiosInstance.delete(`/api/leave/${selectedId}`);
            setLeaves((prev) => prev.filter((leave) => leave.leave_group_id !== selectedId));
            setPopupVisible(false);
        } catch (error) {
            console.error("Error deleting leave:", error);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <Navbar />
            <ConfirmationPopup
                open={isPopupVisible}
                setOpen={setPopupVisible}
                title="Delete Leave Request"
                message="Are you sure you want to delete this leave request? This action cannot be undone."
                confirmText="Delete"
                onConfirm={handleDelete}
            />

            <div className="max-w-7xl mx-auto p-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 mb-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                                <Calendar size={32} />
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold mb-2">My Leave Requests</h1>
                                <p className="text-blue-100">View and manage your leave requests</p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate("/employee/leave/add")}
                            className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold shadow-lg"
                        >
                            <Plus size={20} />
                            <span>Apply Leave</span>
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-xl shadow-lg p-5">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                                <Calendar className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">
                                    {leaves.filter(l => l.status === 0).length}
                                </p>
                                <p className="text-sm text-gray-500">Pending</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-5">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                                <Calendar className="w-6 h-6 text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">
                                    {leaves.filter(l => l.status === 1).length}
                                </p>
                                <p className="text-sm text-gray-500">Approved</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-5">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                                <Calendar className="w-6 h-6 text-rose-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-800">
                                    {leaves.filter(l => l.status === 2).length}
                                </p>
                                <p className="text-sm text-gray-500">Rejected</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                {loading ? (
                    <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading leave requests...</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-800">All Leave Requests</h2>
                            <p className="text-sm text-gray-500 mt-1">Total: {leaves.length} requests</p>
                        </div>

                        {leaves.length === 0 ? (
                            <div className="p-12 text-center text-gray-500">
                                <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                                <p className="text-lg">No leave requests found</p>
                                <p className="text-sm mt-2">Click "Apply Leave" to submit your first request</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">S.No.</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Duration</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Leave Type</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reviewed By</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {leaves.map((leave, index) => (
                                            <tr key={leave.leave_id_PK} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm font-medium text-gray-900">{index + 1}</span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <ProfileAvatar
                                                            className="h-10 w-10"
                                                            url={leave.user_profile_img}
                                                            name={`${leave.first_name || "User"} ${leave.last_name || ""}`}
                                                        />
                                                        <div>
                                                            <p className="font-semibold text-gray-900">
                                                                {leave.first_name || "N/A"} {leave.last_name || ""}
                                                            </p>
                                                            <p className="text-xs text-gray-500">{leave.email}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{leave.formatted_date || "N/A"}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm font-semibold text-gray-900">{leave.leave_name}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-gray-500">
                                                        {leave.rejected_by_first_name 
                                                            ? `${leave.rejected_by_first_name} ${leave.rejected_by_last_name || ""}`
                                                            : "—"
                                                        }
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    {leave.status === 1 ? (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                                            Approved
                                                        </span>
                                                    ) : leave.status === 2 ? (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                                                            Rejected
                                                        </span>
                                                    ) : leave.status === 0 ? (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                                                            Pending
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                                                            Unknown
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    {leave.status === 0 ? (
                                                        <button
                                                            onClick={() => {
                                                                setSelectedId(leave.leave_group_id);
                                                                setPopupVisible(true);
                                                            }}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    ) : (
                                                        <span className="text-gray-300">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Leavelist;
